import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, Inject, Injectable, Signal, signal, untracked} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatStepperModule} from "@angular/material/stepper";
import {StepperSelectionEvent} from "@angular/cdk/stepper";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {TranslateModule} from "@ngx-translate/core";
import {firstValueFrom, Observable} from "rxjs";
import {map} from "rxjs/operators";
import {HttpHeaders} from "@angular/common/http";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {MikrofonVelgerKomponent} from "web-ng2/delt/komponenter/mikrofon-velger/mikrofon-velger";
import {MikrofonPulsKomponent} from "web-ng2/delt/komponenter/mikrofon-puls/mikrofon-puls";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {MøteTranskripsjon, MøteTranskripsjonFeil} from "../../tjenester/MøteTranskripsjon";
import {GruppeId, MøteId, MøteModell, MøtetranskripsjonRessursClient, ProtokollTranskripsjonInformasjonModell, SnakketSpråk, Språk} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from "@angular/material/select";
import {ProtokollDatatjeneste} from "felles-ng2/tjenester/datalager/ProtokollDatatjeneste";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AsyncPipe} from "@angular/common";
import {FormaterZonedDateRelativTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimeRelativPipe";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {SkjemalåsErrorRespons} from "felles-ng2/tjenester/Skjemalåshåndtering";
import {Lydfragment, MikrofonopptakFabrikk} from "felles-ng2/tjenester/Mikrofonopptak";
import {Temporal} from "temporal-polyfill";
import {skrivDurationSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import Duration = Temporal.Duration;

const debug = initDebugger('StartMøteTranskripsjonDialogKomponent');

/**
 * Lar bruker starte transkripsjonen av et møte, i tre steg:
 *
 * 1. bruker velger mikrofon
 * 2. et kort testopptak med valgt mikrofon transkriberes på server og vises, slik at bruker kan
 *    verifisere at mikrofonen fungerer før møtet tas opp
 * 3. bruker informeres om hva som skjer når opptaket avsluttes, og starter opptaket
 *
 * Selve opptaket eies av {@link MøteTranskripsjon}, og lever videre etter at dialogen er lukket.
 */
@Component({
    selector: 'start-moete-transkripsjon-dialog',
    templateUrl: 'start-møte-transkripsjon-dialog.html',
    styleUrls: ['start-møte-transkripsjon-dialog.scss'],
    providers: [],
    imports: [
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        MatStepperModule,
        MatProgressBar,
        MatProgressSpinner,
        TranslateModule,
        TipsKomponent,
        InlineMeldingKomponent,
        BekreftKlikkDirektiv,
        FormaterDurationPipe,
        MikrofonVelgerKomponent,
        MikrofonPulsKomponent,
        AsyncPipe,
        FormaterZonedDateRelativTimePipe,
        FormaterZonedDateTimePipe,
        MatSlideToggle,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StartMøteTranskripsjonDialogKomponent {

    /**
     * Viser dialogen og returnerer mikrofonen brukeren valgte, eller null dersom dialogen ble avbrutt.
     *
     * @param opsjoner.fortsettelse angis når et opptak som var i gang startes på nytt, se
     * {@link Fortsettelsesårsak}. Dialogen forklarer da hvorfor den vises, og tilbyr ikke å slette
     * lydopptakene – de er jo nettopp tatt opp av det samme møtet.
     */
    static async vis(dialog: MatDialog, møte: MøteModell, opsjoner: Partial<Omit<DialogParametre, 'møte'>> = {}): Promise<StartMøteTranskripsjonDialogRespons> {
        const dialogRef = dialog.open<StartMøteTranskripsjonDialogKomponent, DialogParametre, StartMøteTranskripsjonDialogRespons>(
          StartMøteTranskripsjonDialogKomponent,
          {
              data: {møte, fortsettelse: null, ...opsjoner},
              disableClose: true,
              ariaLabelledBy: 'start-møte-transkripsjon-dialog-tittel',
              width: '640px'
          }
        );

        const respons = await firstValueFrom(dialogRef.afterClosed(), {defaultValue: null});
        debug('valgt mikrofon %o', respons);
        return respons ?? null;
    }

    /** Hvor lenge testopptaket varer, kort nok til at det ikke er en byrde og langt nok til at noen setninger blir sagt */
    static readonly standardTestopptaksvarighet = Duration.from({seconds: 10});

    readonly Steg = Steg;

    moete: MøteModell;
    /** Se {@link vis} */
    fortsettelse: Fortsettelsesårsak | null;
    valgtDeviceId = signal<string | null>(null);


    /** Språkene bruker kan velge mellom, med navnet de vises med */
    readonly snakkedeSpraak: { verdi: SnakketSpråk, navn: string }[] = [
        {verdi: SnakketSpråk.norsk, navn: i18n('Norsk')},
        {verdi: SnakketSpråk.engelsk, navn: i18n('Engelsk')}
    ];
    /** Språket det snakkes i møtet. Gruppens språk er forhåndsvalgt, eller språket opptaket hadde når det startes på nytt */
    valgtSpraak = new FormControl<SnakketSpråk>(MøteTranskripsjon.standardSnakketSpråk, {nonNullable: true});
    valgtSpraakSomSignal = toSignal(this.valgtSpraak.valueChanges, {initialValue: MøteTranskripsjon.standardSnakketSpråk});
    /** Feil ved henting av mikrofonene, vises i steget der mikrofonen velges */
    enhetsfeil = signal<string | undefined>(undefined);
    /** Feil ved start av opptaket, vises i siste steg */
    sisteFeil = signal<string | undefined>(undefined);

    /** Lydopptakene møtet alt har, null inntil protokolldetaljene er lastet */
    transkripsjonsinformasjon: Signal<ProtokollTranskripsjonInformasjonModell | null>;

    /** Sann mens slettingen av de eksisterende opptakene pågår, brukes for å låse knappene */
    sletterOpptak = asynkArbeidPågårSignal();

    /** Sann dersom møtet alt har lydopptak, som et nytt opptak i så fall ville kommet på toppen av */
    harEksisterendeOpptak = computed(() => this.fortsettelse == null && (this.transkripsjonsinformasjon()?.antallOpptak ?? 0) > 0);
    skjemalaasErrorRespons = this.møteTranskripsjon.skjemalåsErrorRespons;
    overtaSkjemalaas = new FormControl(false, {nonNullable: true});
    overtaSkjemalaasSomSignal = toSignal(this.overtaSkjemalaas.valueChanges, {initialValue: false});

    /** Sann mens opptaket av møtet startes, slik at bruker ikke rekker å starte det flere ganger */
    starter = asynkArbeidPågårSignal();
    startKnappDeaktivert = computed(() => this.starter() || this.sletterOpptak() || (erIkkeNil(this.skjemalaasErrorRespons()) && !this.overtaSkjemalaasSomSignal()));

    readonly testopptaksvarighet: Duration;
    readonly testopptaksekunder: number;
    testtilstand = signal<Testopptakstilstand>('ikkeStartet');
    /** Det testopptaket ble transkribert til, tom streng når det ikke ble gjenkjent noe tale */
    testtranskripsjon = signal<string | null>(null);
    testfeil = signal<string | undefined>(undefined);
    /** Sekundene som gjenstår av testopptaket mens det tas opp */
    gjenstaaendeSekunder = signal(0);
    /** Hvor langt testopptaket har kommet, i prosent – for fremdriftsindikatoren */
    testfremdrift = computed(() => 100 * (this.testopptaksekunder - this.gjenstaaendeSekunder()) / this.testopptaksekunder);
    testPaagaar = computed(() => this.testtilstand() === 'tarOpp' || this.testtilstand() === 'transkriberer');

    /** Avbryter testopptaket som pågår, uten verdi når det ikke tas noe testopptak */
    private avbrytTestopptak?: AbortController;
    private nedtelling?: ReturnType<typeof setInterval>;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<StartMøteTranskripsjonDialogKomponent, StartMøteTranskripsjonDialogRespons>,
                private møteTranskripsjon: MøteTranskripsjon,
                private avhengigheter: StartMøteTranskripsjonDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                destroyRef: DestroyRef) {
        this.moete = parametre.møte;
        this.fortsettelse = parametre.fortsettelse;
        this.testopptaksvarighet = parametre.testopptaksvarighet ?? StartMøteTranskripsjonDialogKomponent.standardTestopptaksvarighet;
        this.testopptaksekunder = Math.max(1, Math.round(this.testopptaksvarighet.total('seconds')));
        this.transkripsjonsinformasjon = toSignal(this.avhengigheter.transkripsjonsinformasjon(this.moete.id), {initialValue: null});

        void this.forhåndsvelgSpråk();

        //et testopptak gjelder mikrofonen og språket det ble tatt med, så det må tas på nytt når de byttes
        effect(() => {
            this.valgtDeviceId();
            this.valgtSpraakSomSignal();
            untracked(() => this.nullstillTestopptak());
        });

        //mikrofonen skal ikke bli stående på dersom dialogen lukkes midt i et testopptak
        destroyRef.onDestroy(() => this.stoppTestopptak());
    }

    vedEnhetsfeil(feil: Error): void {
        this.enhetsfeil.set(feil.message);
    }

    /** Testopptaket hører til sitt steg, og avbrytes dersom bruker går videre eller tilbake mens det tas opp */
    vedStegbytte(hendelse: StepperSelectionEvent): void {
        if (hendelse.previouslySelectedIndex === Steg.testMikrofon && this.testPaagaar()) this.nullstillTestopptak();
    }

    avbryt() {
        this.dialogRef.close(null);
    }

    async slettEksisterendeOpptak(): Promise<void> {
        await this.sletterOpptak.utfør(() => this.avhengigheter.slettOpptak(this.moete.id));
    }

    /**
     * Tar et kort opptak med valgt mikrofon og lar serveren transkribere det, slik at bruker ser
     * om mikrofonen fanger opp det som blir sagt. Testopptaket blir ikke en del av møtet.
     */
    async taTestopptak(): Promise<void> {
        this.nullstillTestopptak();
        const avbryt = this.avbrytTestopptak = new AbortController();
        this.testtilstand.set('tarOpp');
        this.gjenstaaendeSekunder.set(this.testopptaksekunder);

        try {
            const lydfil = await this.avhengigheter.taOppTestlyd(this.valgtDeviceId(), this.testopptaksvarighet, avbryt.signal, () => this.startNedtelling());
            this.stoppNedtelling();
            if (avbryt.signal.aborted || erNil(lydfil)) return;

            this.testtilstand.set('transkriberer');
            const transkripsjon = await this.avhengigheter.transkriberTestopptak(this.moete.id, lydfil, this.valgtSpraak.value);
            if (avbryt.signal.aborted) return;

            this.testtranskripsjon.set(transkripsjon);
            this.testtilstand.set('ferdig');
        } catch (feil: any) {
            if (avbryt.signal.aborted) return;
            debug('testopptak feilet %o', feil);
            this.testfeil.set(this.oversettelser.oversett(i18n('Testopptaket feilet, kontroller at nettleseren har tilgang til mikrofonen og forsøk på nytt')));
            this.testtilstand.set('feilet');
        } finally {
            this.stoppNedtelling();
            if (this.avbrytTestopptak === avbryt) this.avbrytTestopptak = undefined;
        }
    }

    async start() {
        this.sisteFeil.set(undefined);
        try {
            await this.starter.utfør(() => this.møteTranskripsjon.start(this.moete, this.valgtDeviceId(), this.overtaSkjemalaasSomSignal(), this.valgtSpraak.value));
            this.dialogRef.close({valgtDeviceId: this.valgtDeviceId()});

        } catch (feil: any) {
            if (feil instanceof MøteTranskripsjonFeil) {
                switch (feil.type) {
                    case "startOpptak":
                        this.sisteFeil.set(this.oversettelser.oversett(i18n('Kunne ikke starte lydopptak, vennligst forsøk på nytt')));
                        break;
                    case "pågåendeOpptak":
                        this.sisteFeil.set(this.oversettelser.oversett(i18n('Det pågår allerede et opptak')));
                        break;
                }
            } else if (feil instanceof SkjemalåsErrorRespons) {
                //vi leser SkjemalåsErrorRespons via this.møteTranskripsjon.skjemalåsErrorRespons, ignorerer derfor denne typen feil
            } else {
                this.sisteFeil.set(feil?.message || String(feil));
            }
        }
    }

    //private hjelpemetoder

    /**
     * Et opptak som startes på nytt fortsetter med språket det hadde, ellers forhåndsvelges gruppens
     * språk. Gruppen lastes asynkront, så et valg bruker rekker å gjøre i mellomtiden overstyres ikke.
     */
    private async forhåndsvelgSpråk(): Promise<void> {
        const språk = this.fortsettelse != null ?
          this.møteTranskripsjon.sistBrukteSpråk :
          await this.avhengigheter.gruppespråk(this.moete.eiendeGruppeId);
        if (!this.valgtSpraak.dirty) this.valgtSpraak.setValue(språk);
    }

    /** Avbryter et testopptak som pågår, og glemmer resultatet av det forrige */
    private nullstillTestopptak(): void {
        this.stoppTestopptak();
        this.testtilstand.set('ikkeStartet');
        this.testtranskripsjon.set(null);
        this.testfeil.set(undefined);
    }

    private stoppTestopptak(): void {
        this.avbrytTestopptak?.abort();
        this.avbrytTestopptak = undefined;
        this.stoppNedtelling();
    }

    /** Teller ned fra det tidspunktet mikrofonen faktisk er i gang, ikke mens nettleseren spør om tilgang */
    private startNedtelling(): void {
        this.stoppNedtelling();
        this.nedtelling = setInterval(() => this.gjenstaaendeSekunder.update(sekunder => Math.max(0, sekunder - 1)), 1000);
    }

    private stoppNedtelling(): void {
        if (erIkkeNil(this.nedtelling)) clearInterval(this.nedtelling);
        this.nedtelling = undefined;
    }
}

@Injectable()
export class StartMøteTranskripsjonDialogKomponentAvhengigheter {

    constructor(private protokollDatatjeneste: ProtokollDatatjeneste,
                private gruppeDatatjeneste: GruppeDatatjeneste,
                private møtetranskripsjonRessurs: MøtetranskripsjonRessursClient,
                private mikrofonopptakFabrikk: MikrofonopptakFabrikk) {
    }

    /**
     * Språket det trolig snakkes i gruppens møter, avledet av språket gruppen er satt opp med.
     * Faller tilbake på standardspråket dersom gruppen ikke lar seg laste – valget kan uansett endres av bruker.
     */
    async gruppespråk(gruppeId: GruppeId): Promise<SnakketSpråk> {
        try {
            const gruppe = await firstValueFrom(this.gruppeDatatjeneste.hent(gruppeId));
            return gruppe.oppsett.spraak === Språk.engelsk ? SnakketSpråk.engelsk : SnakketSpråk.norsk;
        } catch (feil) {
            debug('kunne ikke laste gruppen for å finne språket, bruker standardspråket %o', feil);
            return MøteTranskripsjon.standardSnakketSpråk;
        }
    }

    /** Lydopptakene og det genererte protokollforslaget møtet alt har, holdt i takt med datalageret */
    transkripsjonsinformasjon(møteId: MøteId): Observable<ProtokollTranskripsjonInformasjonModell> {
        return this.protokollDatatjeneste.hent(møteId).pipe(map(protokoll => protokoll.transkripsjonInformasjon));
    }

    /** Sletter lydopptakene av møtebehandlingen og det oppsummerte møtereferatet på server. */
    async slettOpptak(møteId: MøteId): Promise<void> {
        await this.protokollDatatjeneste.slettOpptak(møteId);
    }

    /**
     * Tar opp lyd fra mikrofonen i angitt tid, og leverer opptaket som én lydfil.
     *
     * @param vedOpptaksstart kalles når mikrofonen faktisk er i gang, dvs etter at bruker har gitt tilgang
     * @returns lydfila, eller null dersom opptaket ble avbrutt
     */
    taOppTestlyd(deviceId: string | null, varighet: Duration, avbryt: AbortSignal, vedOpptaksstart: () => void): Promise<Lydfragment | null> {
        const varighetMs = varighet.total('milliseconds');
        //lydfila skal ikke deles opp, så grensene for oppdeling settes godt over varigheten
        const opptak = this.mikrofonopptakFabrikk.opprett({deviceId: deviceId ?? undefined, minimumFragmentlengdeMs: varighetMs * 10, maksimumFragmentlengdeMs: varighetMs * 10});

        return new Promise<Lydfragment | null>((resolve, reject) => {
            let lydfil: Lydfragment | null = null;
            let tidsavbrudd: ReturnType<typeof setTimeout> | undefined;

            //abonneres før start, slik at vi ikke går glipp av at mikrofonen kommer i gang
            const abonnement = opptak.vedOpptaksstart.subscribe(() => {
                vedOpptaksstart();
                tidsavbrudd = setTimeout(() => opptak.stopp(), varighetMs);
            });
            abonnement.add(opptak.start().subscribe({
                next: fragment => lydfil = fragment,
                error: reject,
                complete: () => resolve(lydfil)
            }));

            avbryt.addEventListener('abort', () => {
                clearTimeout(tidsavbrudd);
                //avslutter vi abonnementet avbrytes opptaket, og lyden forkastes
                abonnement.unsubscribe();
                resolve(null);
            }, {once: true});
        });
    }

    /** Transkripsjonen av testopptaket, tom streng når det ikke ble gjenkjent noe tale */
    async transkriberTestopptak(møteId: MøteId, lydfil: Lydfragment, språk: SnakketSpråk): Promise<string> {
        const respons = await this.møtetranskripsjonRessurs.testtranskriber(
          møteId,
          lydfil.lyddata,
          {varighet: skrivDurationSerialisert(lydfil.varighet), spraak: språk},
          {headere: new HttpHeaders({'Content-Type': lydfil.lyddata.type || 'application/octet-stream'})}
        ).somPromise();
        return respons.transkripsjon;
    }
}

/**
 * Hvorfor et opptak som var i gang startes på nytt, se MøteLydopptakInnspillingKomponent:
 * - `ingenLyd` – mikrofonen fanget ikke opp lyd
 * - `mikrofonFrakoblet` – mikrofonen ble koblet fra
 * - `mikrofonDempet` – mikrofonen ble dempet
 * - `avbrutt` – opptaket stoppet uventet, se MøteTranskripsjon.avbrudd
 */
export type Fortsettelsesårsak = 'ingenLyd' | 'mikrofonFrakoblet' | 'mikrofonDempet' | 'avbrutt';

/** Stegene i dialogen, i den rekkefølgen de vises */
export enum Steg {
    velgMikrofon = 0,
    testMikrofon = 1,
    start = 2
}

/**
 * Hvor langt testopptaket har kommet:
 * - `ikkeStartet` – bruker har ikke tatt noe testopptak med valgt mikrofon
 * - `tarOpp` – mikrofonen tar opp, se {@link StartMøteTranskripsjonDialogKomponent.gjenstaaendeSekunder}
 * - `transkriberer` – testopptaket er sendt til server og venter på transkripsjonen
 * - `ferdig` – transkripsjonen er mottatt, se {@link StartMøteTranskripsjonDialogKomponent.testtranskripsjon}
 * - `feilet` – opptaket eller transkripsjonen feilet, se {@link StartMøteTranskripsjonDialogKomponent.testfeil}
 */
export type Testopptakstilstand = 'ikkeStartet' | 'tarOpp' | 'transkriberer' | 'ferdig' | 'feilet';

/**
 * Mikrofonen brukeren valgte, eller null dersom dialogen ble avbrutt.
 * `valgtDeviceId` er null når brukeren beholdt standardmikrofonen, derfor er valget pakket i et objekt.
 */
export type StartMøteTranskripsjonDialogRespons = { valgtDeviceId: string | null } | null;

export interface DialogParametre {
    møte: MøteModell;
    /** Se {@link StartMøteTranskripsjonDialogKomponent.vis} */
    fortsettelse: Fortsettelsesårsak | null;
    /** Hvor lenge testopptaket varer, uten verdi brukes {@link StartMøteTranskripsjonDialogKomponent#standardTestopptaksvarighet}. Ment for tester og storyer */
    testopptaksvarighet?: Duration;
}
